import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { CalendarDays, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useToast } from "../context/ToastContext";
import { db } from "../firebase";
import {
  DEFAULT_PRICING,
  STUDIO_MAX_HOURS,
  dateToKey,
  formatCurrency,
  getStudioTimeBoundaries,
  getStudioSlots,
  hoursBetween,
  mergePricing,
} from "../lib/platformData";
import { isPublicPageListed, mergeSiteContent } from "../utils/helpers";

const monthFormatter = new Intl.DateTimeFormat("fr-FR", {
  month: "long",
  year: "numeric",
});

const dayFormatter = new Intl.DateTimeFormat("fr-FR", {
  weekday: "short",
  day: "numeric",
  month: "long",
});

function buildMonth(offset) {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const firstDay = (first.getDay() + 6) % 7;
  const blanks = Array.from({ length: firstDay }, () => null);
  const days = Array.from({ length: daysInMonth }, (_, index) => {
    return new Date(first.getFullYear(), first.getMonth(), index + 1);
  });
  return { first, days: [...blanks, ...days] };
}

export default function BookingPage() {
  const { addItem } = useCart();
  const toast = useToast();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [site, setSite] = useState(() => mergeSiteContent());
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => dateToKey(new Date()));
  const [sessionType, setSessionType] = useState("with_engineer");
  const [startTime, setStartTime] = useState("14:00");
  const [endTime, setEndTime] = useState("16:00");
  const [takenSlots, setTakenSlots] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "settings", "pricing"),
      (snap) => setPricing(mergePricing(snap.exists() ? snap.data() : DEFAULT_PRICING)),
      () => setPricing(DEFAULT_PRICING)
    );
    return () => off();
  }, []);

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "admin", "content"),
      (snap) => setSite(mergeSiteContent(snap.exists() ? snap.data() : {})),
      () => setSite(mergeSiteContent())
    );
    return () => off();
  }, []);

  useEffect(() => {
    if (!selectedDate) return undefined;
    const off = onSnapshot(
      query(collection(db, "studioSlots"), where("date", "==", selectedDate)),
      (snap) => {
        const map = {};
        snap.docs.forEach((slot) => {
          const data = slot.data();
          map[data.hour] = data.status || "pending";
        });
        setTakenSlots(map);
      }
    );
    return () => off();
  }, [selectedDate]);

  useEffect(() => {
    setStartTime("14:00");
    setEndTime("16:00");
  }, [selectedDate]);

  const month = useMemo(() => buildMonth(monthOffset), [monthOffset]);
  const todayKey = dateToKey(new Date());
  const maxDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return dateToKey(date);
  }, []);

  const hourlyRate =
    sessionType === "without_engineer"
      ? Number(pricing.studioWithoutEngineer || 25)
      : Number(pricing.studioWithEngineer || pricing.studioHourly || 40);
  const selectedHours = useMemo(() => hoursBetween(startTime, endTime), [startTime, endTime]);
  const hasTakenInRange = selectedHours.some((hour) => takenSlots[hour]);
  const total = selectedHours.length * hourlyRate;
  const slots = getStudioSlots();
  const boundaries = getStudioTimeBoundaries();
  const studioSettings = site.pages.studio;
  const showContact = isPublicPageListed(site, "contact");

  const selectStart = (time) => {
    setMessage("");
    setStartTime(time);
    const startHour = Number(time.split(":")[0]);
    const endHour = Number(endTime.split(":")[0]);
    if (startHour >= endHour || endHour - startHour > STUDIO_MAX_HOURS) {
      const nextHour = Math.min(24, startHour + Math.min(2, STUDIO_MAX_HOURS));
      setEndTime(nextHour === 24 ? "24:00" : `${String(nextHour).padStart(2, "0")}:00`);
    }
  };

  const selectEnd = (time) => {
    setMessage("");
    const duration = Number(time.split(":")[0]) - Number(startTime.split(":")[0]);
    if (duration <= 0 || duration > STUDIO_MAX_HOURS) return;
    setEndTime(time);
  };

  const addToCart = () => {
    if (!selectedDate || !selectedHours.length || selectedHours.length > STUDIO_MAX_HOURS) {
      setMessage("Choisis une date, une heure de debut et une heure de fin.");
      toast.error("Sélection incomplète");
      return;
    }
    if (hasTakenInRange) {
      setMessage("Cette plage contient deja un creneau pris.");
      toast.error("Créneau indisponible", "Choisis une autre plage.");
      return;
    }
    const sessionLabel =
      sessionType === "without_engineer"
        ? "Studio sans ingénieur"
        : "Studio avec ingénieur";
    addItem({
      type: "studio",
      name: sessionLabel,
      sessionType,
      sessionLabel,
      date: selectedDate,
      startTime,
      endTime,
      hours: selectedHours,
      duration: selectedHours.length,
      hourlyRate,
      total,
      currency: pricing.currency || "EUR",
      paymentEnabled: studioSettings.paymentEnabled === true,
    });
    toast.success("Séance ajoutée au panier", `${startTime} - ${endTime}`);
    navigate("/panier");
  };

  if (studioSettings.bookingEnabled === false) {
    return (
      <main className="grid min-h-[78vh] place-items-center bg-black px-6 py-24 text-white">
        <section className="max-w-2xl rounded-lg border border-white/10 bg-zinc-950 p-8 text-center md:p-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">
            Studio privé
          </p>
          <h1 className="mt-4 text-4xl font-black uppercase md:text-6xl">
            Réservation indisponible
          </h1>
          <p className="readable-copy mt-5 text-lg text-gray-300">
            {studioSettings.privateMessage}
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/studio" className="rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black">
              Voir le studio
            </Link>
            {showContact && (
              <Link to="/contact" className="rounded-full border border-white/15 px-5 py-3 text-sm font-bold uppercase">
                Nous contacter
              </Link>
            )}
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black px-6 py-24 text-white md:px-20">
      <section className="mx-auto max-w-7xl">
        <p className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
          Reservation studio
        </p>
        <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div>
            <h1 className="text-5xl font-black uppercase leading-none md:text-7xl">
              Reserve une seance.
            </h1>
            <p className="readable-copy mt-4 max-w-2xl text-gray-400">
              Selection visuelle sur 1 an, puis choix des heures disponibles. Le creneau
              est bloque quand tu valides le panier.
            </p>
          </div>
          <div className="rounded-full border border-white/15 px-5 py-3 text-sm font-bold uppercase">
            {formatCurrency(hourlyRate)} / heure
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_420px]">
          <section className="rounded-lg border border-white/10 bg-zinc-950 p-4 md:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <button
                onClick={() => setMonthOffset((value) => Math.max(0, value - 1))}
                disabled={monthOffset === 0}
                className="rounded-full border border-white/15 p-2 disabled:opacity-30"
                aria-label="Mois precedent"
              >
                <ChevronLeft size={18} />
              </button>
              <h2 className="text-xl font-black uppercase md:text-2xl">
                {monthFormatter.format(month.first)}
              </h2>
              <button
                onClick={() => setMonthOffset((value) => Math.min(12, value + 1))}
                disabled={monthOffset === 12}
                className="rounded-full border border-white/15 p-2 disabled:opacity-30"
                aria-label="Mois suivant"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-2 text-center text-xs font-bold uppercase tracking-wide text-gray-500">
              {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>

            <div className="mt-3 grid grid-cols-7 gap-2">
              {month.days.map((day, index) => {
                if (!day) return <div key={`blank-${index}`} className="aspect-square" />;
                const key = dateToKey(day);
                const disabled = key < todayKey || key > maxDate;
                const active = selectedDate === key;

                return (
                  <button
                    key={key}
                    disabled={disabled}
                    onClick={() => setSelectedDate(key)}
                    className={`aspect-square rounded-lg border text-sm font-bold transition ${
                      active
                        ? "border-red-500 bg-red-600 text-white"
                        : "border-white/10 bg-black hover:border-white/40"
                    } ${disabled ? "cursor-not-allowed opacity-25" : ""}`}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
          </section>

          <aside className="rounded-lg border border-white/10 bg-zinc-950 p-4 md:p-6">
            <div className="mb-5 flex items-center gap-3">
              <CalendarDays className="text-red-500" size={22} />
              <div>
                <h2 className="text-2xl font-black uppercase">Creneaux</h2>
                <p className="text-sm text-gray-400">
                  {selectedDate ? dayFormatter.format(new Date(`${selectedDate}T12:00:00`)) : "Choisis une date"}
                </p>
              </div>
            </div>

            <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                onClick={() => setSessionType("with_engineer")}
                className={`rounded-lg border p-4 text-left transition ${
                  sessionType === "with_engineer" ? "border-red-500 bg-red-600/20" : "border-white/10 bg-black hover:border-white/40"
                }`}
              >
                <span className="block text-sm font-bold uppercase">Avec ingénieur</span>
                <span className="mt-1 block text-xl font-black">{formatCurrency(pricing.studioWithEngineer || pricing.studioHourly || 40)}/h</span>
              </button>
              <button
                onClick={() => setSessionType("without_engineer")}
                className={`rounded-lg border p-4 text-left transition ${
                  sessionType === "without_engineer" ? "border-red-500 bg-red-600/20" : "border-white/10 bg-black hover:border-white/40"
                }`}
              >
                <span className="block text-sm font-bold uppercase">Sans ingénieur</span>
                <span className="mt-1 block text-xl font-black">{formatCurrency(pricing.studioWithoutEngineer || 25)}/h</span>
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Début</p>
                <div className="max-h-[280px] space-y-2 overflow-y-auto pr-1">
                  {slots.map((hour) => {
                    const active = startTime === hour;
                    const blocked = Boolean(takenSlots[hour]);
                    return (
                      <button
                        key={hour}
                        disabled={blocked}
                        onClick={() => selectStart(hour)}
                        className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition ${
                          active
                            ? "border-red-500 bg-red-600"
                            : "border-white/10 bg-black hover:border-white/40"
                        } ${blocked ? "cursor-not-allowed opacity-35" : ""}`}
                      >
                        <span className="inline-flex items-center gap-2 font-bold">
                          <Clock size={16} /> {hour}
                        </span>
                        <span className="text-xs uppercase tracking-wide text-gray-300">
                          {blocked ? "pris" : active ? "début" : "dispo"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Fin</p>
                <div className="max-h-[280px] space-y-2 overflow-y-auto pr-1">
                  {boundaries.slice(1).map((hour) => {
                    const active = endTime === hour;
                    const duration = Number(hour.split(":")[0]) - Number(startTime.split(":")[0]);
                    const disabled = duration <= 0 || duration > STUDIO_MAX_HOURS;
                    const range = hoursBetween(startTime, hour);
                    const blocked = range.some((slot) => takenSlots[slot]);
                    return (
                      <button
                        key={hour}
                        disabled={disabled || blocked}
                        onClick={() => selectEnd(hour)}
                        className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition ${
                          active
                            ? "border-red-500 bg-red-600"
                            : "border-white/10 bg-black hover:border-white/40"
                        } ${disabled || blocked ? "cursor-not-allowed opacity-35" : ""}`}
                      >
                        <span className="inline-flex items-center gap-2 font-bold">
                          <Clock size={16} /> {hour}
                        </span>
                        <span className="text-xs uppercase tracking-wide text-gray-300">
                          {blocked ? "pris" : active ? "fin" : "choisir"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-5 max-h-[160px] space-y-2 overflow-y-auto pr-1">
              {slots.map((hour) => {
                const selected = selectedHours.includes(hour);
                const blocked = Boolean(takenSlots[hour]);
                return (
                  <div
                    key={hour}
                    className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition ${
                      selected
                        ? "border-red-500 bg-red-600/40"
                        : "border-white/10 bg-black"
                    } ${blocked ? "cursor-not-allowed opacity-35" : ""}`}
                  >
                    <span className="inline-flex items-center gap-2 font-bold">
                      <Clock size={16} /> {hour}
                    </span>
                    <span className="text-xs uppercase tracking-wide text-gray-300">
                      {blocked ? "pris" : selected ? "selectionne" : "dispo"}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 rounded-lg border border-white/10 bg-black p-4">
              <div className="flex justify-between text-sm text-gray-400">
                <span>Heures</span>
                <span>{selectedHours.length}h / {STUDIO_MAX_HOURS}h</span>
              </div>
              <div className="mt-2 flex justify-between text-sm text-gray-400">
                <span>Type</span>
                <span>{sessionType === "without_engineer" ? "Sans ingénieur" : "Avec ingénieur"}</span>
              </div>
              <div className="mt-2 flex justify-between text-sm text-gray-400">
                <span>Plage</span>
                <span>{startTime} - {endTime}</span>
              </div>
              <div className="mt-2 flex justify-between text-sm text-gray-400">
                <span>Prix horaire</span>
                <span>{formatCurrency(hourlyRate)}</span>
              </div>
              <div className="mt-4 flex justify-between border-t border-white/10 pt-4 text-lg font-black">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            {message && <p className="mt-4 text-sm text-yellow-200">{message}</p>}
            <button
              onClick={addToCart}
              className="mt-5 w-full rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black transition hover:bg-red-600 hover:text-white"
            >
              Ajouter au panier
            </button>
            <Link to="/panier" className="mt-3 inline-flex w-full justify-center rounded-full border border-white/15 px-5 py-3 text-sm font-bold uppercase">
              Voir le panier
            </Link>
          </aside>
        </div>
      </section>
    </main>
  );
}
